import { getGatedIdacBackendUrl } from '../env';
import { useCallback } from 'react';
import { useAppSelector } from '../../stores/redux.store';
import type { UserAction } from '../../actions/login-actions';

export type CheckUsernameV2SuccessResponse = Omit<Response, 'status' | 'json'> & {
  status: 200;
  json(): Promise<UserAction[]>;
};

/** Narrows a check-username response by status, trusting the API contract without parsing its body. */
export const isCheckUsernameV2Success = (
  response: Response
): response is CheckUsernameV2SuccessResponse => response.status === 200;

/**
 * Provides a standalone auxiliary check-username call outside the normal login context,
 * for example when checking whether an account exists. It does not dispatch login actions,
 * navigate, or interpret the response's login flow instructions.
 *
 * Returns an on-demand callback accepting a username. Callers own request timing and response
 * handling: HTTP errors resolve with the raw Response; network errors reject the promise.
 * Use isCheckUsernameV2Success to narrow a 200 response so json() returns UserAction[].
 */
export const useFetchV2CheckUsername = () => {
  const csrfToken = useAppSelector(state => state.csrfToken);

  return useCallback(
    (username: string): Promise<Response> => {
      const path = getGatedIdacBackendUrl('/rest/v2/check-username');
      return fetch(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken },
        body: JSON.stringify({ username }),
      });
    },
    [csrfToken]
  );
};
